"use client"

import { Fragment, useState, useRef } from "react"
import {
  TextField,
  Typography,
  Paper,
  InputAdornment,
  Popper,
  ClickAwayListener,
  List,
  ListItem,
  ListItemText,
  Box,
} from "@mui/material"
import SearchIcon from "@mui/icons-material/Search"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"

function SelectField(props) {
  const {
    label,
    size,
    disabled,
    multiple,
    value,
    selected,
    onSelect,
    onBlur,
    name,
    error,
    options = [],
    placeholder,
    register, // <-- accept register here
  } = props

  const [open, setOpen] = useState(false)
  const [searchValue, setSearchValue] = useState("")
  const anchorRef = useRef(null)

  const filteredOptions = options.filter((option) =>
    (option.label || option.name || "").toLowerCase().includes(searchValue.toLowerCase())
  )

  // *For Handle Change
  const handleChange = (newValue) => {
    setOpen(false)
    if (typeof newValue === "string") {
      onSelect(newValue)
      return
    }
    onSelect(newValue)
  }

  const handleMainFieldClick = () => {
    if (!disabled) {
      setOpen(!open)
    }
  }

  const handleClickAway = () => {
    setOpen(false)
    setSearchValue("")
  }

  return (
    <Fragment>
      <ClickAwayListener onClickAway={handleClickAway}>
        <Box sx={{ position: "relative", width: "100%" }}>
          {label && (
            <label
              style={{
                color: "#a3abb2",
                fontWeight: 600,
                fontFamily: "Maven Pro, sans-serif",
              }}
            >
              {label}
            </label>
          )}

          <TextField
            ref={anchorRef}
            name={name}
            onBlur={onBlur}
            value={selected?.name || ""}
            placeholder={placeholder || label}
            error={!!error}
            disabled={disabled}
            onClick={handleMainFieldClick}
            InputProps={{
              readOnly: true,
              startAdornment: selected ? (
                selected.icon ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, pl: 0 }}>
                    {selected.icon}
                    &nbsp;
                  </Box>
                ) : selected.image ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, pl: 0 }}>
                    <Box component="img" src={selected?.image} width="30px" />
                    &nbsp;
                  </Box>
                ) : (
                  ""
                )
              ) : null,
              endAdornment: (
                <InputAdornment position="end" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  {selected && !disabled && (
                    <Box
                      component="span"
                      onClick={(e) => {
                        e.stopPropagation(); // prevent popper toggle
                        onSelect(null); // clear selection
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        
                        color: "#E0E6ED",
                        cursor: "pointer",
                        "&:hover": { backgroundColor: "#4A5A6C" },
                      }}
                    >
                      ✕
                    </Box>
                  )}

                  <ExpandMoreIcon
                    sx={{
                      color: "#8A9BA8",
                      cursor: disabled ? "default" : "pointer",
                      transform: open ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.2s",
                    }}
                  />
                </InputAdornment>
              ),

            }}
            sx={{
              width: "100%",
              mt: 1,
              cursor: disabled ? "default" : "pointer",
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#273346",
                height: "47px",
                borderRadius: "8px",
                border: "1px solid #3A4A5C",
                color: "#a3abb2",
                cursor: disabled ? "default" : "pointer",
                "&:hover": {
                  borderColor: disabled ? "#3A4A5C" : "#4A5A6C",
                },
                "&.Mui-focused": {
                  borderColor: "#5A6A7C",
                  boxShadow: "none",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
              },
              "& .MuiInputBase-input": {
                color: "#a3abb2",
                fontSize: "15px",
                cursor: disabled ? "default" : "pointer",
              },
              "& .MuiInputBase-input::placeholder": {
                color: "#8A9BA8",
                opacity: 1,
              },
            }}
          />

          {/* ✅ Hidden input so RHF can validate */}
          <input
            type="hidden"
            name={name}
            {...register}
            value={selected?.id || ""}   // <-- submit id only
            readOnly
          />


          <Popper
            open={open}
            anchorEl={anchorRef.current}
            placement="bottom-start"
            sx={{ zIndex: 1300, width: anchorRef.current?.offsetWidth }}
          >
            <Paper
              sx={{
                backgroundColor: "#273346",
                border: "1px solid #3A4A5C",
                borderRadius: "8px",
                marginTop: "4px",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
                width: "100%",
              }}
            >
              <Box sx={{ p: 2, borderBottom: "1px solid #3A4A5C" }}>
                <TextField
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder="Search"
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: "#8A9BA8", fontSize: "18px" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#3A4A5C",
                      borderRadius: "6px",
                      border: "1px solid #4A5A6C",
                      color: "#E0E6ED",
                      "&:hover": {
                        borderColor: "#5A6A7C",
                      },
                      "&.Mui-focused": {
                        borderColor: "#6A7A8C",
                        boxShadow: "none",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                    },
                    "& .MuiInputBase-input": {
                      color: "#E0E6ED",
                      fontSize: "14px",
                      padding: "8px 12px",
                    },
                    "& .MuiInputBase-input::placeholder": {
                      color: "#8A9BA8",
                      opacity: 1,
                    },
                  }}
                />
              </Box>

              <List sx={{ padding: 0, maxHeight: "200px", overflow: "auto" }}>
                {filteredOptions.length > 0 ? (
                  filteredOptions.map((option, index) => (
                    <ListItem
                      key={option.value || index}
                      onClick={() => handleChange(option)}
                      sx={{
                        cursor: "pointer",
                        color: "#E0E6ED",
                        fontSize: "14px",
                        padding: "12px 16px",
                        borderBottom:
                          index < filteredOptions.length - 1
                            ? "1px solid #3A4A5C"
                            : "none",
                        "&:hover": {
                          backgroundColor: "#3A4A5C",
                        },
                        "&:last-child": {
                          borderBottom: "none",
                        },
                      }}
                    >
                      {/* icon + text wrapper */}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {option.icon ? (
                          option.icon
                        ) : option.image ? (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, pl: 0 }}>
                            <Box component="img" src={option?.image} width="30px" />
                            &nbsp;
                          </Box>
                        ) : (
                          ""
                        )}
                        <ListItemText
                          primary={option.label || option.name}
                          sx={{
                            "& .MuiListItemText-primary": {
                              fontSize: "14px",
                              color: "#E0E6ED",
                            },
                          }}
                        />
                      </Box>
                    </ListItem>
                  ))
                ) : (
                  <ListItem sx={{ color: "#8A9BA8", fontSize: "14px", padding: "12px 16px" }}>
                    <ListItemText primary="No options found" />
                  </ListItem>
                )}
              </List>
            </Paper>
          </Popper>
        </Box>
      </ClickAwayListener>

      {error && (
        <Typography variant="caption" color="error" sx={{ textAlign: "left", mt: 1 }}>
          {error}
        </Typography>
      )}
    </Fragment>
  )
}

export default SelectField
